/**
 * ThreadFooter — the reader's sticky footer (plan N5).
 *
 *   [↩ Reply all   new thread]   [↪ Forward all   whole thread]   Replying as …
 *
 * ## There is deliberately no plain "Reply" here
 *
 * Reply is per-message, on the `↩` in a message header, because that is the
 * only place the user can see WHICH message they are answering. A footer Reply
 * has to pick one on the user's behalf, and the message it picks (newest? the
 * one you opened on?) is invisible at the moment of clicking. The prototype
 * drops it for that reason; this component does too.
 *
 * ## The annotations are load-bearing, not decoration
 *
 * `new thread` and `whole thread` are what make the threading model legible
 * BEFORE the user commits: a reply-all starts a fresh exchange, a forward
 * carries the entire conversation to someone who has not seen any of it.
 * Rendering the buttons without them leaves two similar-looking controls with
 * materially different blast radii. They live inside the button, muted at
 * 11.5px, exactly as the prototype draws them.
 *
 * ## Identity lives here, not in the thread composers (#659 D3)
 *
 * The prototype draws no identity row in any thread composer: "Replying as …"
 * in this footer is the one statement of who a reply leaves from. So the From
 * CHOICE lives here too. Where a real choice exists (more than one option and
 * a handler) it renders the shared `ReplyAsSelector`; otherwise it is the
 * fixed line, with the provider badge and the lock explanation as its tooltip.
 * Every identity prop is optional, and a caller passing none gets exactly the
 * text-only footer this component has always drawn.
 */
import React from 'react';
import { CornerUpLeft, CornerUpRight } from 'lucide-react';
import ReplyAsSelector from './ReplyAsSelector';

/**
 * What the identity slot says while no address has resolved. This was
 * `ReplyAsSelector`'s `emptyHint` inside the card before #659 moved identity
 * out of it, and without it the footer would say nothing at all.
 */
const IDENTITY_STATE_TEXT = {
    checking: 'Checking mailbox…',
    none: 'No mailbox connected',
};

/** One footer action: a verb, and the threading consequence under it. */
function FooterAction({ icon: Icon, label, annotation, onClick, disabled, testId }) {
    const isDisabled = disabled || typeof onClick !== 'function';
    return (
        <button
            type="button"
            disabled={isDisabled}
            data-testid={testId}
            onClick={isDisabled ? undefined : onClick}
            className={`inline-flex items-center gap-2 h-[34px] px-3 rounded-[9px]
                        border text-[13px]
                        ${isDisabled
                            ? 'border-stone-200 text-stone-300 cursor-not-allowed'
                            : 'border-stone-300 text-stone-700 bg-white hover:bg-stone-100'}`}
        >
            <Icon className="w-[15px] h-[15px] flex-shrink-0" />
            <span>{label}</span>
            <span
                className={`text-[11.5px] ${isDisabled ? 'text-stone-300' : 'text-[#A29B95]'}`}
                data-testid={`${testId}-annotation`}
            >
                {annotation}
            </span>
        </button>
    );
}

export default function ThreadFooter({
    /**
     * `() => void`. The parent decides which message the thread-level reply
     * answers — this component never picks one, for the reason in the header
     * note above.
     */
    onReplyAll,
    onForwardAll,
    /**
     * The ADDRESS a reply would leave from, or null while it is unknown. Never
     * a provider id: "Replying as gmail" was #484.
     */
    replyingAs,
    disabled = false,
    // ── The From choice (#659 D3). All optional. ─────────────────────────
    /** `[{ value?, address, provider?, sendable? }]`, the same shape the picker takes. */
    fromOptions,
    /** The selected option's `value ?? address`. */
    fromValue,
    onFromChange,
    /** `(opt) => node`, e.g. the provider badge. */
    renderBadge,
    /** A badge beside the fixed line (the personal lane's locked mailbox). */
    fromBadge,
    /** Tooltip on the fixed line, e.g. why the mailbox is locked. */
    lockedHint,
    /** `null` | `'checking'` | `'none'` — shown only while `replyingAs` is null. */
    identityState = null,
}) {
    const hasChoice = Array.isArray(fromOptions) && fromOptions.length > 1
        && typeof onFromChange === 'function';
    let identity = null;
    if (hasChoice) {
        identity = (
            <span
                className="ml-auto flex items-center gap-2 min-w-0 text-[11.5px] text-[#A29B95]"
                data-testid="thread-footer-from"
            >
                <span className="flex-shrink-0">Replying as</span>
                <ReplyAsSelector
                    options={fromOptions}
                    value={fromValue}
                    onChange={onFromChange}
                    label="Replying as"
                    renderBadge={renderBadge}
                    variant="inline"
                    size="sm"
                    disabled={disabled}
                />
            </span>
        );
    } else if (replyingAs) {
        identity = (
            <span
                className="ml-auto flex items-center gap-[6px] min-w-0 text-[11.5px] text-[#A29B95]"
                title={lockedHint || undefined}
                data-testid="thread-footer-replying-as"
            >
                <span className="truncate">Replying as {replyingAs}</span>
                {fromBadge}
            </span>
        );
    } else if (IDENTITY_STATE_TEXT[identityState]) {
        identity = (
            <span
                className="ml-auto text-[11.5px] text-[#A29B95] italic"
                data-testid="thread-footer-identity-state"
            >
                {IDENTITY_STATE_TEXT[identityState]}
            </span>
        );
    }

    return (
        <div
            className="sticky bottom-0 z-10 flex items-center gap-2 px-5 py-3
                       border-t border-stone-200 bg-white"
            data-testid="thread-footer"
        >
            <FooterAction
                icon={CornerUpLeft}
                label="Reply all"
                annotation="new thread"
                onClick={onReplyAll}
                disabled={disabled}
                testId="thread-footer-reply-all"
            />
            <FooterAction
                icon={CornerUpRight}
                label="Forward all"
                annotation="whole thread"
                onClick={onForwardAll}
                disabled={disabled}
                testId="thread-footer-forward-all"
            />
            {identity}
        </div>
    );
}
